/**
 * Report detail — split layout matching design handoff screen-report.jsx.
 *
 * Left panel:  Cover notes editor + editable time entries table
 * Right panel: Live PDF preview (iframe with rendered HTML)
 * Bottom bar:  Recipients info, Reject, Save draft, Approve & send
 *
 * SPEC §7.5: "the review phase is about refining time entry descriptions
 * so they make sense to the client — not approving a pre-made PDF."
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Check,
  ChevronRight,
  Clock,
  Download,
  ExternalLink,
  Pencil,
  Send,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import type { TimeEntry } from '@beacon/core';

import { Avatar } from '@/components/beacon/avatar.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  useApproveReport,
  useReport,
  useSendReport,
  useUpdateCoverNotes,
} from '@/hooks/use-reports.js';
import { useUpdateTimeEntryNotes } from '@/hooks/use-time-entry-mutations.js';
import { useIsClient } from '@/hooks/use-viewer.js';
import { cn } from '@/lib/cn.js';

import { RejectReportDialog } from './RejectReportDialog.js';
import { SendReportDialog } from './SendReportDialog.js';

export function ReportDetail() {
  const { id } = useParams();
  const detail = useReport(id);
  const reportId = detail.data?.report.id ?? '';
  const updateNotes = useUpdateTimeEntryNotes(reportId);
  const updateCover = useUpdateCoverNotes(reportId);
  const approve = useApproveReport(reportId);
  const send = useSendReport(reportId);
  const isClient = useIsClient();
  const [sendOpen, setSendOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [cover, setCover] = useState('');
  const [savingIdx, setSavingIdx] = useState<string | null>(null);
  const [editingEntries, setEditingEntries] = useState(false);

  // Sync cover note from server data
  useEffect(() => {
    if (detail.data?.report) {
      setCover(detail.data.report.coverNotes ?? '');
    }
  }, [detail.data?.report?.id, detail.data?.report?.coverNotes]);

  if (detail.isLoading) return <ReportDetailSkeleton />;
  if (detail.isError || !detail.data) {
    return (
      <div className="p-8 text-ink-300">
        Report not found.{' '}
        <Link to="/reports" className="text-beacon-300 hover:underline">
          Back to queue
        </Link>
      </div>
    );
  }

  const { report, contract, timeEntries, defaultRecipients } = detail.data;
  const totalHours = timeEntries.reduce(
    (s, e) => s + (typeof e.hours === 'number' ? e.hours : Number(e.hours)),
    0
  );
  const canApprove = report.status === 'review';
  const canSend = report.status === 'approved';
  const canReject = report.status === 'review' || report.status === 'draft';
  const isEditable = report.status === 'draft' || report.status === 'review';

  const statusBadge = () => {
    if (report.status === 'draft')
      return <Badge tone="amber" className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-warn-500" />Draft — pending review</Badge>;
    if (report.status === 'review')
      return <Badge tone="violet" className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" />In review</Badge>;
    if (report.status === 'approved')
      return <Badge tone="blue">Approved</Badge>;
    if (report.status === 'sent')
      return <Badge tone="green">Sent</Badge>;
    return <Badge>{report.status}</Badge>;
  };

  const handleNoteBlur = async (entry: TimeEntry, newValue: string) => {
    if (newValue === entry.notes) return;
    setSavingIdx(entry.id);
    try {
      await updateNotes.mutateAsync({ entryId: entry.id, notes: newValue });
    } catch {
      toast.error('Failed to save note');
    } finally {
      // Brief delay to show "synced" state
      setTimeout(() => setSavingIdx(null), 1200);
    }
  };

  return (
    <div className="flex h-full min-h-0">
      {/* ── Left panel: editable entries ── */}
      <div className="flex-1 min-w-0 flex flex-col border-r border-ink-600">
        {/* Header */}
        <div className="px-6 pt-4 pb-3 border-b border-ink-600 bg-ink-850">
          <div className="flex items-center gap-2 text-[12px] text-ink-300 mb-1">
            <Link to="/reports" className="hover:text-ink-100">Reports</Link>
            <ChevronRight size={12} />
            <span className="mono">{monthLabel(report.periodStartDate)}</span>
            <span className="mx-1">·</span>
            {statusBadge()}
          </div>
          <div className="flex items-center gap-3">
            <ContractLogo name={contract.name} size={32} />
            <div className="flex-1 min-w-0">
              <h1 className="text-[18px] font-semibold">
                Monthly report — {contract.name} · {monthLabel(report.periodStartDate)}
              </h1>
              <div className="text-[12px] text-ink-300">
                Period {formatDateRange(report.periodStartDate, report.periodEndDate)} ·{' '}
                {contract.type.toUpperCase()}{' '}
                {'monthlyHours' in contract ? `${contract.monthlyHours}h/month` : ''} ·{' '}
                Owner {defaultRecipients[0] ?? '—'}
              </div>
            </div>
            <div className="flex items-center gap-2 text-[11.5px] text-ink-300 mono">
              <Clock size={12} /> Autosaved
            </div>
          </div>
        </div>

        {/* Cover notes */}
        {isEditable && (
          <div className="px-6 py-4 border-b border-ink-600">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] uppercase tracking-[0.06em] text-ink-300 font-semibold">
                Cover notes / summary
              </span>
              <span className="text-[11px] text-ink-400 mono">Markdown supported</span>
            </div>
            <textarea
              rows={3}
              value={cover}
              onChange={(e) => setCover(e.target.value)}
              onBlur={() => {
                const serverValue = detail.data?.report.coverNotes ?? '';
                if (cover !== serverValue) {
                  updateCover.mutate(cover);
                }
              }}
              placeholder="Add a summary for this report period…"
              className="w-full text-[13px] bg-ink-850 border border-beacon-400/30 focus:border-beacon-400 rounded-md px-3 py-2 text-ink-100 focus-ring resize-none leading-relaxed"
            />
            {updateCover.isPending && (
              <div className="mt-1 text-[11px] text-beacon-300 mono inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-beacon-400 animate-pulse" /> saving…
              </div>
            )}
          </div>
        )}

        {/* Time entries table */}
        <div className="flex-1 overflow-auto scrollbar-thin">
          <div className="px-6 py-3 flex items-center">
            <div className="text-[13px] font-semibold">Time entries</div>
            <div className="ml-auto flex items-center gap-3 text-[12px] text-ink-300">
              <span className="mono">{timeEntries.length} entries · {totalHours.toFixed(2)}h</span>
              {isEditable && !isClient && (
                <Button
                  variant={editingEntries ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setEditingEntries((p) => !p)}
                >
                  <Pencil size={12} />
                  {editingEntries ? 'Done editing' : 'Edit notes'}
                </Button>
              )}
              <Button variant="ghost" size="sm">
                <Download size={13} />Export CSV
              </Button>
            </div>
          </div>
          <table className="w-full text-[12.5px]">
            <thead className="sticky top-0 bg-ink-900 z-10">
              <tr className="text-left text-[10.5px] uppercase tracking-wider text-ink-300 font-semibold border-b border-ink-600">
                <th className="py-2 px-6 w-[80px]">Date</th>
                <th className="py-2 pr-3 w-[160px]">Staff member</th>
                <th className="py-2 pr-3 w-[80px]">Hours</th>
                <th className="py-2 pr-6">
                  Notes{editingEntries && (
                    <span className="text-beacon-300 normal-case tracking-normal"> · editing · writes back to Harvest</span>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {timeEntries.map((e) => (
                <TimeEntryRow
                  key={e.id}
                  entry={e}
                  editable={editingEntries}
                  savingId={savingIdx}
                  onBlur={handleNoteBlur}
                />
              ))}
            </tbody>
          </table>
          <div className="h-20" />
        </div>

        {/* Sticky action bar */}
        {!isClient && (
          <div className="border-t border-ink-600 bg-ink-850 px-6 py-3 flex items-center gap-3">
            <div className="flex items-center gap-2 text-[12px] text-ink-300">
              <Users size={14} />
              <span>
                Will send to{' '}
                <span className="text-ink-100">
                  {defaultRecipients[0] ?? '—'}
                </span>
                {defaultRecipients.length > 1 && (
                  <span>, cc {defaultRecipients.slice(1).join(', ')}</span>
                )}
              </span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {canReject && (
                <Button variant="ghost" onClick={() => setRejectOpen(true)}>
                  <X size={13} />Reject
                </Button>
              )}
              {isEditable && (
                <Button variant="outline">Save draft</Button>
              )}
              {canApprove && (
                <Button
                  variant="primary"
                  disabled={approve.isPending}
                  onClick={() => {
                    approve.mutate(undefined, {
                      onSuccess: () =>
                        toast.success('Report approved', {
                          description: 'Artifact stored. Ready to send to client.',
                        }),
                    });
                  }}
                >
                  <Send size={13} />
                  {approve.isPending ? 'Approving…' : 'Approve & send PDF'}
                </Button>
              )}
              {canSend && (
                <Button
                  variant="primary"
                  disabled={send.isPending}
                  onClick={() => setSendOpen(true)}
                >
                  <Send size={13} />Send to client
                </Button>
              )}
              {report.status === 'sent' && (
                <Button variant="primary" disabled>Sent</Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Right panel: PDF preview ── */}
      <div className="w-[560px] shrink-0 bg-ink-850 flex flex-col">
        <div className="h-12 px-4 border-b border-ink-600 flex items-center">
          <span className="text-[10.5px] uppercase tracking-wider text-ink-300 font-semibold">
            Live preview
          </span>
          <span className="ml-2 text-[11.5px] text-ink-400 mono">
            {contract.name.replace(/[^A-Za-z0-9]+/g, '_')}_{monthLabel(report.periodStartDate).replace(' ', '_')}.pdf
          </span>
          <div className="ml-auto flex items-center gap-1">
            <Button variant="ghost" size="sm">100%</Button>
            {report.pdfR2Key && (
              <a
                href={`/api/reports/${encodeURIComponent(report.id)}/download`}
                target="_blank"
                rel="noreferrer"
              >
                <Button variant="ghost" size="sm"><ExternalLink size={13} /></Button>
              </a>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-auto scrollbar-thin p-5 bg-ink-900">
          {report.htmlRendered ? (
            <iframe
              title={`${contract.name} — ${monthLabel(report.periodStartDate)}`}
              srcDoc={report.htmlRendered}
              sandbox="allow-same-origin"
              className="w-full h-full min-h-[800px] bg-white rounded-md shadow-lg border-0"
            />
          ) : (
            <div className="mx-auto w-full max-w-[500px] bg-ink-800 rounded-md p-12 text-center text-[13px] text-ink-300">
              No preview available. Generate a report first.
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <SendReportDialog
        open={sendOpen}
        contractName={contract.name}
        periodLabel={monthLabel(report.periodStartDate)}
        defaultRecipients={defaultRecipients}
        isSending={send.isPending}
        onClose={() => { if (!send.isPending) setSendOpen(false); }}
        onSend={(recipients) => {
          send.mutate(recipients, {
            onSuccess: () => {
              setSendOpen(false);
              toast.success('Report queued for send', {
                description: `${recipients.length} recipient${recipients.length === 1 ? '' : 's'} — delivery happens in the next minute.`,
              });
            },
          });
        }}
      />
      <RejectReportDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        reportId={report.id}
        contractName={contract.name}
        periodLabel={monthLabel(report.periodStartDate)}
      />
    </div>
  );
}

/* ── Time entry row with inline editable notes ── */
function TimeEntryRow({
  entry,
  editable,
  savingId,
  onBlur,
}: {
  entry: TimeEntry;
  editable: boolean;
  savingId: string | null;
  onBlur: (entry: TimeEntry, newValue: string) => void;
}) {
  const [value, setValue] = useState(entry.notes);
  const hours = typeof entry.hours === 'number' ? entry.hours : Number(entry.hours);
  const isSaving = savingId === entry.id;
  const isSynced = savingId !== entry.id;

  // Sync from server if notes change externally
  useEffect(() => { setValue(entry.notes); }, [entry.notes]);

  return (
    <tr className="border-b border-ink-600/60 align-top hover:bg-ink-850/50">
      <td className="py-3 px-6 mono text-[12px] text-ink-200 whitespace-nowrap">
        {formatShortDate(entry.spentDate)}
      </td>
      <td className="py-3 pr-3">
        <div className="flex items-center gap-1.5">
          <Avatar name={entry.userName} size={20} />
          <span className="text-ink-100">{entry.userName}</span>
        </div>
      </td>
      <td className="py-3 pr-3 mono text-[13px] font-semibold">{hours.toFixed(2)}</td>
      <td className="py-3 pr-6">
        {editable ? (
          <div className="relative">
            <textarea
              rows={2}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onBlur={() => onBlur(entry, value)}
              className="w-full text-[12.5px] bg-ink-850 border border-ink-600 rounded-md px-2 py-1.5 text-ink-100 focus:border-beacon-400 focus:bg-ink-800 focus-ring resize-none leading-relaxed"
            />
            <div className="mt-1 flex items-center gap-2 text-[11px] mono">
              {isSaving ? (
                <span className="text-beacon-300 inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-beacon-400 animate-pulse" />
                  saving to Harvest…
                </span>
              ) : isSynced ? (
                <span className="text-ok-400 inline-flex items-center gap-1">
                  <Check size={11} /> synced to Harvest
                </span>
              ) : null}
              {entry.harvestEntryId && (
                <span className="text-ink-400">· entry #{entry.harvestEntryId.slice(-4)}</span>
              )}
            </div>
          </div>
        ) : (
          <p className="text-[12.5px] text-ink-200 whitespace-pre-wrap leading-relaxed">
            {entry.notes || <span className="text-ink-400 italic">No notes</span>}
          </p>
        )}
      </td>
    </tr>
  );
}

/* ── Contract logo (same as sidebar) ── */
const CONTRACT_HUES = ['#E57D2F', '#4C8C5B', '#7A4A9D', '#2E86AB', '#D64045', '#6B8F71', '#9B5DE5', '#F15BB5'] as const;

function ContractLogo({ name, size = 24 }: { name: string; size?: number }) {
  const tag = name.split(/[\s-]+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const color = CONTRACT_HUES[name.length % CONTRACT_HUES.length];
  return (
    <span
      className="inline-flex items-center justify-center font-bold tracking-tight text-ink-900 shrink-0 rounded-[5px]"
      style={{ width: size, height: size, background: color, fontSize: Math.max(10, size * 0.42) }}
    >
      {tag}
    </span>
  );
}

/* ── Skeleton ── */
function ReportDetailSkeleton() {
  return (
    <div className="flex h-full min-h-0" aria-busy="true">
      <div className="flex-1 min-w-0 flex flex-col border-r border-ink-600">
        <div className="px-6 pt-4 pb-3 border-b border-ink-600 bg-ink-850 space-y-2">
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-5 w-80" />
          <Skeleton className="h-3 w-56" />
        </div>
        <div className="px-6 py-4 border-b border-ink-600 space-y-2">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-16 w-full rounded-md" />
        </div>
        <div className="flex-1 p-6 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-4">
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-10 flex-1 rounded-md" />
            </div>
          ))}
        </div>
      </div>
      <div className="w-[560px] shrink-0 bg-ink-850 flex flex-col">
        <div className="h-12 px-4 border-b border-ink-600 flex items-center">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-40 ml-2" />
        </div>
        <div className="flex-1 p-5 bg-ink-900">
          <Skeleton className="w-full h-full min-h-[400px] rounded-md opacity-50" />
        </div>
      </div>
    </div>
  );
}

/* ── Helpers ── */
function monthLabel(ymd: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(ymd);
  if (!m) return ymd;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
}

function formatDateRange(start: string, end: string): string {
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const fmt = (d: Date): string =>
    d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', timeZone: 'Australia/Sydney' });
  return `${fmt(s)} – ${fmt(e)} ${s.getFullYear()}`;
}

function formatShortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', timeZone: 'Australia/Sydney' });
}
